// AGC: User types for Firebase Authentication and role-based access control
// AGC: Supports multi-user hierarchy: Admin -> Manager -> SubManager -> User
// AGC: Integrates with subscription system for freemium features
// AGC: Includes user subtypes for granular permission control

import type { SubscriptionTier, UserSubscription, UsageTracker } from './subscription'

/**
 * AGC: User role types for the application
 * AGC: - admin: Full system access, can manage all users
 * AGC: - manager: Can create sub-managers (PREMIUM) and users (PRO+)
 * AGC: - submanager: Can only create users (no sub-managers)
 * AGC: - user: Basic access, no user management
 */
export type UserRole = 'admin' | 'manager' | 'submanager' | 'user'

/**
 * AGC: User subtypes for granular permission control
 * AGC: Only applies to users with role='user' created by managers
 * AGC: - operador: Fields (view), Tasks (own only), Weather (view), NO financial
 * AGC: - tecnico: Fields (all), Tasks (all), Weather (view), NO financial
 * AGC: - financeiro: Financial only, NO fields/tasks/weather
 * AGC: - completo: Full access except user management
 */
export type UserSubtype = 'operador' | 'tecnico' | 'financeiro' | 'completo' | null

/**
 * AGC: User profile stored in Firestore
 * AGC: Collection: users/{uid}
 * AGC: All account information comes from Firebase - no hardcoded values
 */
export interface UserProfile {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  phoneNumber: string | null
  role: UserRole
  parentManagerId: string | null  // AGC: null for top-level managers/admins
  createdBy: string | null        // AGC: uid of manager who created this user
  biometricEnabled: boolean
  biometricCredentialId: string | null // AGC: WebAuthn credential ID for web biometrics
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string
  // AGC: Extended profile fields stored in Firebase
  whatsappPhone?: string | null
  location?: string | null
  state?: string | null
  city?: string | null
  farmName?: string | null
  farmArea?: string | null
  
  // AGC: Subscription system (freemium model)
  subscriptionTier?: SubscriptionTier  // AGC: 'free' | 'pro' | 'premium'
  
  // AGC: Legacy field for backward compatibility with voice-only tier
  /** @deprecated Use subscriptionTier instead */
  voiceTier?: 'free' | 'paid'
  
  // AGC: Consultant profile ID (if user is a registered consultant)
  consultantProfileId?: string | null
  
  // AGC: Team/organization info (for multi-user support)
  organizationId?: string | null
  teamRole?: 'owner' | 'admin' | 'member' | 'viewer'
  
  // AGC: User subtype for granular permissions (only for role='user')
  subtype?: UserSubtype

  // AGC: Farm-scoped role (canonical, preferred over legacy role+subtype). Written at
  // AGC: self-signup as 'owner' so new users aren't demoted by backend lazy migration.
  farmRole?: import('./rbac').FarmRole
}

/**
 * AGC: Firebase Auth user combined with Firestore profile
 */
export interface AuthUser {
  // AGC: From Firebase Auth
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  phoneNumber: string | null
  emailVerified: boolean
  // AGC: From Firestore profile
  profile: UserProfile | null
}

/**
 * AGC: Auth state for context
 */
export interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  isFirstLogin: boolean // AGC: Used to prompt biometric setup
}

/**
 * AGC: Login credentials for email/password auth
 */
export interface LoginCredentials {
  email: string
  password: string
}

/**
 * AGC: Registration data for new users
 */
export interface RegisterData {
  email: string
  password: string
  displayName: string
  phoneNumber?: string
}

/**
 * AGC: Data for creating a new user by a manager
 * AGC: subtype is required when role='user'
 */
export interface CreateUserData {
  email: string
  password: string
  displayName: string
  role: 'submanager' | 'user'
  phoneNumber?: string
  subtype?: UserSubtype  // AGC: Required for role='user'
}

/**
 * AGC: Role hierarchy levels for permission checking
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 4,
  manager: 3,
  submanager: 2,
  user: 1,
}

/**
 * AGC: Check if a role can manage another role
 */
export const canRoleManage = (managerRole: UserRole, targetRole: UserRole): boolean => {
  // AGC: Admin can manage everyone except other admins
  if (managerRole === 'admin') {
    return targetRole !== 'admin'
  }
  // AGC: Manager can create submanagers and users
  if (managerRole === 'manager') {
    return targetRole === 'submanager' || targetRole === 'user'
  }
  // AGC: SubManager can only create users
  if (managerRole === 'submanager') {
    return targetRole === 'user'
  }
  // AGC: Users cannot manage anyone
  return false
}

/**
 * AGC: Get allowed roles that a user can create
 */
export const getAllowedRolesToCreate = (role: UserRole): UserRole[] => {
  switch (role) {
    case 'admin':
      return ['manager', 'submanager', 'user']
    case 'manager':
      return ['submanager', 'user']
    case 'submanager':
      return ['user']
    default:
      return []
  }
}

/**
 * AGC: Check if user has permission to access admin panel
 */
export const canAccessAdmin = (role: UserRole): boolean => {
  return role === 'admin'
}

/**
 * AGC: Check if user can manage other users
 */
export const canManageUsers = (role: UserRole): boolean => {
  return role === 'admin' || role === 'manager' || role === 'submanager'
}

/**
 * AGC: Check if user can create sub-managers
 */
export const canCreateSubManager = (role: UserRole): boolean => {
  return role === 'admin' || role === 'manager'
}

/**
 * AGC: Get role display name for UI
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const displayNames: Record<UserRole, string> = {
    admin: 'Administrador',
    manager: 'Gerente',
    submanager: 'Sub-gerente',
    user: 'Usuário',
  }
  return displayNames[role]
}

/**
 * AGC: Get role badge color for UI
 */
export const getRoleBadgeColor = (role: UserRole): string => {
  const colors: Record<UserRole, string> = {
    admin: 'bg-red-500 text-white',
    manager: 'bg-blue-500 text-white',
    submanager: 'bg-purple-500 text-white',
    user: 'bg-gray-500 text-white',
  }
  return colors[role]
}

/**
 * AGC: Extended user with subscription data
 * AGC: Used when full subscription info is needed
 */
export interface UserWithSubscription extends AuthUser {
  subscription: UserSubscription | null
  currentUsage: UsageTracker | null
}

/**
 * AGC: Team member in an organization
 */
export interface TeamMember {
  id: string
  userId: string
  displayName: string
  email: string
  photoURL?: string
  teamRole: 'owner' | 'admin' | 'member' | 'viewer'
  joinedAt: string
  invitedBy: string
  isActive: boolean
}

/**
 * AGC: Organization/Farm group for multi-user support
 */
export interface Organization {
  id: string
  name: string
  ownerId: string
  subscriptionTier: SubscriptionTier
  members: TeamMember[]
  maxMembers: number | null  // AGC: Based on subscription tier
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Get subscription tier display badge
 */
export const getSubscriptionBadgeColor = (tier: SubscriptionTier): string => {
  const colors: Record<SubscriptionTier, string> = {
    free: 'bg-emerald-100 text-emerald-700',
    pro: 'bg-blue-100 text-blue-700',
    premium: 'bg-yellow-100 text-yellow-700',
  }
  return colors[tier]
}

/**
 * AGC: Get subscription tier display name
 */
export const getSubscriptionTierName = (tier: SubscriptionTier): string => {
  const names: Record<SubscriptionTier, string> = {
    free: 'Gratuito',
    pro: 'Pro',
    premium: 'Premium',
  }
  return names[tier]
}

// AGC: UserPermissions interface + getPermissions() removed in v3.9.3
// AGC: Runtime permissions now live in types/rbac.ts (FarmPermission / ROLE_PERMISSIONS)
// AGC: Legacy callers should use useRBAC() or the compat shim usePermissions()

/**
 * AGC: Get user subtype display name for UI
 */
export const getSubtypeDisplayName = (subtype: UserSubtype): string => {
  if (!subtype) return 'Usuário'
  
  const displayNames: Record<NonNullable<UserSubtype>, string> = {
    operador: 'Operador',
    tecnico: 'Técnico',
    financeiro: 'Financeiro',
    completo: 'Usuário Completo',
  }
  return displayNames[subtype]
}

/**
 * AGC: Get user subtype description for UI
 */
export const getSubtypeDescription = (subtype: UserSubtype): string => {
  if (!subtype) return 'Acesso básico'
  
  const descriptions: Record<NonNullable<UserSubtype>, string> = {
    operador: 'Talhões, Tarefas (próprias), Clima',
    tecnico: 'Talhões, Tarefas (todas), Clima',
    financeiro: 'Apenas informações financeiras',
    completo: 'Acesso completo (exceto gestão de usuários)',
  }
  return descriptions[subtype]
}

/**
 * AGC: Get user subtype badge color for UI
 */
export const getSubtypeBadgeColor = (subtype: UserSubtype): string => {
  if (!subtype) return 'bg-gray-100 text-gray-700'
  
  const colors: Record<NonNullable<UserSubtype>, string> = {
    operador: 'bg-orange-100 text-orange-700',
    tecnico: 'bg-cyan-100 text-cyan-700',
    financeiro: 'bg-green-100 text-green-700',
    completo: 'bg-indigo-100 text-indigo-700',
  }
  return colors[subtype]
}

/**
 * AGC: All available user subtypes for forms
 */
export const USER_SUBTYPES: NonNullable<UserSubtype>[] = ['operador', 'tecnico', 'financeiro', 'completo']
